import {useEffect, useMemo, useState} from "react";
import {
    buildCustomerProfileLifecycleOptions,
    resolveDefaultCustomerProfileLifecycleId,
} from "~/components/entityProfile/events/buildCustomerProfileLifecycleOptions";
import {ProfileCustomerEventsLifecycleFilterPill} from "~/components/entityProfile/events/profileCustomerEventsTabFilters";
import type {CustomerProfileTabContext} from "~/components/entityProfile/profileTabRegistry";
import type {ProfileTabRenderProps} from "~/components/entityProfile/types";
import {useCustomerProfileEventsJourney} from "~/components/entityProfile/useCustomerProfileEventsJourney";
import {EventsTab} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/event-containers/$eventContainerId/index/-eventsTab";
import {AnankeSkeleton} from "@vesta/ananke/components/anankeSkeleton";
import {AnankeText} from "@vesta/ananke/components/anankeText";
import {TableErrorComponent} from "~/components/ottopilotDataViewer/tableErrorComponent";
import type {EventContainerWithEvents} from "~/utilities/apis/api";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";

function ProfileCustomerEventsTabSkeleton() {
    return (
        <div className="grid gap-ds-space-150 px-ds-space-200 py-ds-space-150">
            <AnankeSkeleton width={280} height={32} borderRadius="4px" />
            <AnankeSkeleton width="100%" height={120} borderRadius="4px" />
            <AnankeSkeleton width="100%" height={120} borderRadius="4px" />
        </div>
    );
}

export function ProfileCustomerEventsTabContent({
    tabRenderProps,
    context,
}: {
    tabRenderProps: ProfileTabRenderProps;
    context: CustomerProfileTabContext;
}) {
    const lifecycleOptions = context.events.lifecycleOptions;
    const [selectedLifecycleId, setSelectedLifecycleId] = useState<Uuid | null>(() =>
        resolveDefaultCustomerProfileLifecycleId(lifecycleOptions),
    );

    useEffect(() => {
        if (selectedLifecycleId === null && lifecycleOptions.length > 0) {
            setSelectedLifecycleId(resolveDefaultCustomerProfileLifecycleId(lifecycleOptions));
            return;
        }
        if (
            selectedLifecycleId !== null
            && lifecycleOptions.some((option) => option.id === selectedLifecycleId) !== true
        ) {
            setSelectedLifecycleId(resolveDefaultCustomerProfileLifecycleId(lifecycleOptions));
        }
    }, [lifecycleOptions, selectedLifecycleId]);

    const selectedLifecycle = useMemo(() => {
        if (selectedLifecycleId === null) {
            return null;
        }
        return lifecycleOptions.find((option) => option.id === selectedLifecycleId) ?? null;
    }, [lifecycleOptions, selectedLifecycleId]);
    const selectedEventContainerId = selectedLifecycle?.eventContainerId ?? null;

    const journeyOverride = useMemo((): EventContainerWithEvents | undefined => {
        if (
            context.events.linkedJourneyWithEventsOverride !== undefined
            && selectedEventContainerId !== null
            && context.events.linkedJourneyWithEventsOverride.id === selectedEventContainerId
        ) {
            return context.events.linkedJourneyWithEventsOverride;
        }
        return undefined;
    }, [context.events.linkedJourneyWithEventsOverride, selectedEventContainerId]);

    const {journey, isLoading, isError} = useCustomerProfileEventsJourney({
        eventContainerId: selectedEventContainerId,
        eventContainerTemplateId: selectedLifecycle?.eventContainerTemplateId,
        journeyWithEventsOverride: journeyOverride,
        enabled: context.events.isOptionsLoading !== true,
    });

    if (tabRenderProps.kind !== "customer") {
        return null;
    }

    const showLifecycleLoading = context.events.isOptionsLoading === true;
    const showJourneyLoading = showLifecycleLoading !== true && selectedEventContainerId !== null && isLoading === true;

    const lifecycleFilterPill = (
        <ProfileCustomerEventsLifecycleFilterPill
            options={lifecycleOptions}
            value={selectedLifecycleId}
            onChange={setSelectedLifecycleId}
            isDisabled={showLifecycleLoading === true}
        />
    );

    return (
        <div className="flex h-full min-h-0 flex-col overflow-hidden">
            <div className="min-h-0 flex-1 overflow-hidden">
                {showLifecycleLoading === true || showJourneyLoading === true ? (
                    <ProfileCustomerEventsTabSkeleton />
                ) : null}
                {showLifecycleLoading !== true && showJourneyLoading !== true && isError === true ? (
                    <div className="px-ds-space-200 py-ds-space-150">
                        <TableErrorComponent error="Failed to load events for this lifecycle" />
                    </div>
                ) : null}
                {showLifecycleLoading !== true
                && showJourneyLoading !== true
                && isError !== true
                && selectedEventContainerId === null ? (
                    <div className="flex h-full min-h-0 flex-col overflow-hidden">
                        <div className="shrink-0 px-ds-space-200 py-ds-space-100">
                            {lifecycleFilterPill}
                        </div>
                        <div className="px-ds-space-200 py-ds-space-150">
                            <AnankeText color="color.text.subtlest">
                                Events for this lifecycle will appear when it is linked to a journey.
                            </AnankeText>
                        </div>
                    </div>
                ) : null}
                {showLifecycleLoading !== true
                && showJourneyLoading !== true
                && isError !== true
                && journey !== null ? (
                    <EventsTab
                        key={journey.id}
                        journey={journey}
                        presentation="profile"
                        className="h-full min-h-0"
                        dismissFooterEngagementPanels={context.dismissFooterEngagementPanels}
                        registerDismissEventRowEngagement={context.registerDismissEventRowEngagement}
                        profileFilterLeadingContent={lifecycleFilterPill}
                    />
                ) : null}
            </div>
        </div>
    );
}
